import { useMemo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { bindActionCreators } from 'redux';

import useUser from 'hooks/useUser';
import { getReports } from 'store/reports/actions';
import { getUsers } from 'store/users/actions';
import { ReportingRoleEnum } from 'utils/enums';
import { getSearchParams } from 'utils/helpers';

const useReports = () => {
  const dispatch = useDispatch();
  const { search } = useLocation();
  const { appUser } = useUser();

  const userData = useSelector((state) => state.users);

  const {
    data: reports,
    isLoading: isLoadingReports,
    error: errorReports,
  } = useSelector((state) => state.reports);

  const { impersonateUserId } = useMemo(
    () => getSearchParams(search),
    [search],
  );

  const users = useMemo(() => userData.data ?? [], [userData.data]);

  const userOptions = useMemo(
    () => users.map(({ id, name }) => ({ value: id, label: name })),
    [users],
  );

  const impersonatedUser = useMemo(() => {
    if (!impersonateUserId) return null;
    return users.find(({ id }) => id === Number(impersonateUserId));
  }, [users, impersonateUserId]);

  const currentUser = useMemo(
    () => impersonatedUser ?? appUser,
    [appUser, impersonatedUser],
  );

  const selectedOption = useMemo(() => {
    if (!impersonatedUser) return null;
    return { value: impersonatedUser.id, label: impersonatedUser.name };
  }, [impersonatedUser]);

  const hasWorkSpace = useMemo(
    () => currentUser?.burnCenterId
      && currentUser?.reportingRole !== ReportingRoleEnum.DASHBOARD,
    [currentUser],
  );

  const { getReports: fetchReports } = useMemo(
    () => bindActionCreators({ getReports }, dispatch),
    [dispatch],
  );

  const fetchUsers = useCallback(() => {
    dispatch(
      getUsers({
        CanAccessReports: true,
      }),
    );
  }, [dispatch]);

  return {
    currentUser,
    errorReports,
    fetchReports,
    fetchUsers,
    hasWorkSpace,
    impersonateUserId,
    isLoadingReports,
    reports,
    selectedOption,
    userOptions,
  };
};

export default useReports;
